import { useEffect, useRef, useState } from "react";
import socketIOClient from "socket.io-client";
import { useAuth } from "context/Auth";
import { notification } from "antd";
import useSound from "use-sound";
import kaChingSfx from "../../../assets/sounds/ka-ching.mp3";

const ENDPOINT = process.env.REACT_APP_WS_URL;
const ENV = process.env.REACT_APP_ENVIRONMENT?.toLowerCase() || "development";

const SocketIO = () => {
  const { auth, setAuth } = useAuth();
  const [play, { stop }] = useSound(kaChingSfx, { volume: 0.2 });
  const [soundReady, setSoundReady] = useState(false);
  const socketRef = useRef(null);

  // Preload sound on first user interaction (bypass autoplay block)
  useEffect(() => {
    const enableSound = () => {
      play();
      stop();
      setSoundReady(true);
      if (ENV === "development") console.log("🔊 Sound preloaded");
    };

    window.addEventListener("click", enableSound, { once: true });
    return () => window.removeEventListener("click", enableSound);
  }, [play, stop]);

  useEffect(() => {
    const socket = socketIOClient(ENDPOINT, { transports: ["websocket", "polling"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      if (ENV === "development") console.log("✅ Socket connected:", socket.id);
      socket.emit("user-connected", { token: auth.data.token, socket_id: socket.id });
    });

    socket.on("disconnect", () => {
      if (ENV === "development") console.log("🚫 Socket disconnected");
    });

    socket.on("precheckout", (data) => {
      notification.info({
        placement: "bottomLeft",
        duration: 5,
        message: "Pré-Checkout",
        description: `Novo pré-checkout de "${data.product_name}" efetuado por ${data.buyer}`,
      });
    });

    socket.on("sale", (data) => {
      notification.info({
        placement: "bottomLeft",
        duration: 5,
        message: "Venda Efetuada",
        description: `Nova venda de "${data.product_name}" efetuada por ${data.buyer}`,
      });
      if (soundReady) play(); // ✅ only play if preload was triggered
    });

    socket.on("checkout-events", (data) => {
      // Handle any future checkout events if needed
    });

    socket.on("permissions-updated", (data) => {
      if (auth.data?.active_entity?.id === auth.data?.id) {
        // If the active entity is the user's personal account, no need to update permissions in real-time
        return;
      }
      notification.info({
        placement: "bottomLeft",
        duration: 5,
        message: "Permissões Atualizadas",
        description: "As tuas permissões foram atualizadas.",
      });

      setAuth((prevAuth) => ({
        ...prevAuth,
        data: {
          ...prevAuth.data,
          permissions: data.permissions,
        },
      }));
    });

    // Clean up socket connection when component unmounts
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        if (ENV === "development") console.log("🔌 Socket disconnected and cleaned up");
      }
    };
  }, [auth.data.token, play, soundReady]);

  return null;
};

export default SocketIO;
